import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Modal, Btn } from "@/components/ui-kit";
import { Printer } from "lucide-react";
import { REPAIR_OUTCOMES, REPAIR_TYPES, findMeta, fmtDate } from "@/lib/constants";

// Printable batch outward gatepass / delivery note listing every unit serial.
export default function GatepassDoc({ gp, onClose }) {
  if (!gp) return null;
  const outcome = findMeta(REPAIR_OUTCOMES, gp.repair_outcome)?.label || gp.repair_outcome;
  const rtype = findMeta(REPAIR_TYPES, gp.repair_type || "new_repair").label;
  return (
    <Modal open onClose={onClose} title={`Outward Gatepass · ${gp.gatepass_number}`} wide>
      <div className="space-y-4">
        <div id="print-label" className="mx-auto max-h-[65vh] w-full max-w-[760px] overflow-y-auto rounded-md border-2 border-black bg-white p-6 text-black" data-testid="gatepass-doc">
          <div className="flex items-start justify-between border-b-2 border-black pb-3">
            <div>
              <img src="/logo-amax.png" alt="amax" className="mb-2 h-6 w-auto" />
              <div className="font-heading text-xl font-extrabold uppercase leading-tight">Outward Gatepass / Delivery Note</div>
              <div className="font-mono text-xs">RepairOps Workshop · {gp.gatepass_number}</div>
              <div className="mt-1 font-mono text-[11px]">Dispatched {fmtDate(gp.dispatch_date)} by {gp.dispatched_by_name}</div>
            </div>
            <QRCodeSVG value={JSON.stringify({ code: gp.gatepass_number, t: "gatepass", b: gp.batch_number })} size={84} level="M" />
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-1 py-3 font-mono text-[11px] sm:grid-cols-3">
            <Row k="Batch" v={gp.batch_number} />
            <Row k="Item" v={gp.equipment_name} />
            <Row k="Model" v={`${gp.brand || ""} ${gp.model_number || ""}`.trim() || "—"} />
            <Row k="Qty" v={`${gp.quantity} units`} />
            <Row k="Type" v={rtype} />
            <Row k="Outcome" v={outcome} />
            <Row k="Warranty" v={gp.warranty_provided ? `${gp.warranty_period} · till ${fmtDate(gp.warranty_until)}` : "None"} />
            <Row k="Courier" v={gp.courier_details || "—"} />
            <Row k="Receiver" v={gp.client_signoff_name || "—"} />
          </div>

          <table className="w-full border-collapse font-mono text-[11px]">
            <thead>
              <tr className="border-y-2 border-black text-left">
                <th className="py-1 pr-2">#</th><th className="py-1 pr-2">Job No.</th><th className="py-1 pr-2">Serial Number</th><th className="py-1 text-right">Rcvd ✓</th>
              </tr>
            </thead>
            <tbody>
              {gp.units.map((u, i) => (
                <tr key={u.job_id} className="border-b border-black/30" data-testid={`gp-unit-${u.job_id}`}>
                  <td className="py-0.5 pr-2">{i + 1}</td><td className="py-0.5 pr-2">{u.job_number}</td><td className="py-0.5 pr-2 font-bold">{u.serial_number}</td><td className="py-0.5 text-right">☐</td>
                </tr>
              ))}
            </tbody>
          </table>
          {gp.notes && <div className="mt-2 font-mono text-[11px]"><b>Notes:</b> {gp.notes}</div>}

          <div className="mt-6 grid grid-cols-3 gap-6 font-mono text-[10px] uppercase">
            {["Dispatched by", "Security / Gate", "Received by"].map((s) => (
              <div key={s} className="border-t border-black pt-1">{s}<div className="mt-5 text-[9px] normal-case text-black/60">Name · Sign · Date</div></div>
            ))}
          </div>
        </div>

        <div className="no-print flex items-center justify-between gap-2">
          <span className="font-mono text-[11px] text-muted-foreground" data-testid="gp-unit-count">{gp.units.length} units on this gatepass</span>
          <div className="flex gap-2">
            <Btn variant="outline" onClick={onClose} data-testid="gatepass-close-btn">Close</Btn>
            <Btn onClick={() => window.print()} data-testid="print-gatepass-btn"><Printer size={15} /> Print Gatepass</Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}

const Row = ({ k, v }) => <div className="flex gap-2"><span className="w-16 shrink-0 font-bold">{k}</span><span>{v}</span></div>;
